/**
 * KOA-15840 — which BPMN elements differ between two exported process versions.
 *
 * The Version History compare view feeds `WorkflowBpmnDiff`, which marks a set of element
 * ids on both diagrams. A version is stored as BPMN XML, so the diff is taken at the XML
 * level: an id present on one side only was added or removed, and an id on both sides whose
 * `name` changed was renamed. `branching/branch-workflow-captions.ts` holds the sibling for
 * the WF JSON model (`workflowChangedNodeIds`) — same output, different input medium.
 *
 * Pure and framework-free: `DOMParser` is the only dependency (no new package). Nothing here
 * uses locale rules — ids and names are compared with `===`, and the result is ordered by
 * `sort()`'s UTF-16 code-unit order, never `localeCompare`.
 *
 * Deliberate limits:
 *  - Only `id` and `name` are compared. A moved node, a re-pointed sequence flow or an edited
 *    extension element is not reported.
 *  - Layout is ignored (the `BPMNDiagram` subtree is skipped), so a diagram that was only
 *    re-laid out reports no changes.
 *  - If either side is missing or unparseable the result is empty. Reporting "every id on the
 *    other side" would light up a whole diagram and read as a diff that was never computed.
 */

/**
 * localName of the Diagram Interchange root. Everything below it describes layout and carries
 * its own ids (`Activity_1_di`), which are not diagram element ids — pruning the subtree keeps
 * both false diffs and unmarkable ids out of the result.
 */
const DIAGRAM_INTERCHANGE_ROOT: string = "BPMNDiagram";

/**
 * Elements that carry an `id` but are never a marked diagram element: the document root and the
 * structural containers. Their children still matter, so these are skipped rather than pruned.
 * `process` is in the list because a collaboration renders the `participant`, not the process —
 * a process rename therefore surfaces through the participant instead.
 */
const STRUCTURAL_CONTAINERS: ReadonlySet<string> = new Set<string>([
  "definitions",
  "collaboration",
  "process",
  "laneSet"
]);

/** Marker element browsers and jsdom put in the document when XML parsing fails. */
const PARSER_ERROR: string = "parsererror";

/** id → `name` attribute ("" when the element has none) for every element worth comparing. */
const collectElements = (element: Element, into: Map<string, string>): void => {
  const tag: string = element.localName;
  if (tag === DIAGRAM_INTERCHANGE_ROOT) return;

  const id: string | null = element.getAttribute("id");
  if (id && !STRUCTURAL_CONTAINERS.has(tag)) {
    into.set(id, element.getAttribute("name") ?? "");
  }

  const children: HTMLCollection = element.children;
  for (let index = 0; index < children.length; index += 1) {
    collectElements(children[index], into);
  }
};

/** Parses one side. Returns null — never throws — for absent, blank or unparseable XML. */
const parseVersionElements = (xml: string | undefined): Map<string, string> | null => {
  const source: string = (xml ?? "").trim();
  if (!source) return null;

  let document: Document | undefined;
  try {
    document = new DOMParser().parseFromString(source, "text/xml");
  } catch {
    // Some engines throw instead of producing a <parsererror> document.
    return null;
  }

  const root: Element | null | undefined = document?.documentElement;
  if (!root) return null;
  // Both shapes are seen in the wild: the error as the document element, or nested inside it.
  if (root.localName === PARSER_ERROR) return null;
  if (root.getElementsByTagName(PARSER_ERROR).length > 0) return null;

  const elements: Map<string, string> = new Map<string, string>();
  collectElements(root, elements);
  return elements;
};

/**
 * The diagram element ids that differ between two versions' BPMN XML: added, removed or renamed.
 * De-duplicated and sorted, so the value is stable across calls — `WorkflowBpmnDiff` joins it
 * into a cache key and re-imports both diagrams whenever that key changes.
 */
export const versionChangedNodeIds = (beforeXml: string | undefined, afterXml: string | undefined): string[] => {
  const before: Map<string, string> | null = parseVersionElements(beforeXml);
  const after: Map<string, string> | null = parseVersionElements(afterXml);
  if (!before || !after) return [];

  const changed: Set<string> = new Set<string>();

  before.forEach((beforeName: string, id: string): void => {
    const afterName: string | undefined = after.get(id);
    // undefined means the id is gone (removed); an exact string mismatch means renamed.
    if (afterName === undefined || afterName !== beforeName) changed.add(id);
  });

  after.forEach((_afterName: string, id: string): void => {
    if (!before.has(id)) changed.add(id);
  });

  // Bare sort(): UTF-16 code-unit order, identical under every locale.
  return [...changed].sort();
};
